import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, EyeOff, Pencil } from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { GridLayoutCore } from "@/components/dashboard/GridLayout";
import { DashboardFilterProvider } from "@/contexts/DashboardFilterContext";
import { ActiveFilterBar } from "@/components/dashboard/GridLayout";
import { loadAnalystOverride, saveAnalystOverride } from "@/lib/dashboardStore";
import { useSectionDashboard } from "@/hooks/useSectionDashboard";
import { placementKey } from "@/types/widget";
import type { WidgetPlacement } from "@/types/widget";
import type { AnalystOverride } from "@/types/dashboard";

export default function DashboardViewerPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { config, isLoading } = useSectionDashboard(slug ?? "");

  const [override, setOverride] = useState<AnalystOverride>(() =>
    config
      ? loadAnalystOverride(config.id)
      : { dashboardId: "", hiddenInstanceIds: [], sectionLayouts: {} },
  );

  // Persist override whenever it changes
  function mutateOverride(updater: (prev: AnalystOverride) => AnalystOverride) {
    setOverride((prev) => {
      const next = updater(prev);
      saveAnalystOverride(next);
      return next;
    });
  }

  function toggleHideWidget(instanceId: string) {
    mutateOverride((prev) => ({
      ...prev,
      hiddenInstanceIds: prev.hiddenInstanceIds.includes(instanceId)
        ? prev.hiddenInstanceIds.filter((id) => id !== instanceId)
        : [...prev.hiddenInstanceIds, instanceId],
    }));
  }

  function updateSectionLayout(sectionId: string, placements: WidgetPlacement[]) {
    mutateOverride((prev) => ({
      ...prev,
      sectionLayouts: { ...prev.sectionLayouts, [sectionId]: placements },
    }));
  }

  // Track collapsed state locally (can also be persisted via override if desired)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const init = new Set<string>();
    config?.sections.forEach((s) => {
      if (s.collapsed) init.add(s.id);
    });
    return init;
  });

  function toggleCollapse(sectionId: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  if (!config) {
    return (
      <AdminLayout
        pageTitle="Dashboard"
        breadcrumbs={[
          { title: "Management", href: "/admin" },
          { title: "Dashboards", href: "/admin/dashboards" },
        ]}
      >
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          {isLoading ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <>
              <p className="text-sm text-muted">Dashboard not found.</p>
              <Button variant="outline" size="sm" onClick={() => navigate("/admin/dashboards")}>
                Back to Dashboards
              </Button>
            </>
          )}
        </div>
      </AdminLayout>
    );
  }

  const sortedSections = [...config.sections].sort((a, b) => a.order - b.order);
  const hiddenCount = override.hiddenInstanceIds.length;

  return (
    <AdminLayout
      pageTitle={config.name}
      breadcrumbs={[
        { title: "Management", href: "/admin" },
        { title: "Dashboards", href: "/admin/dashboards" },
      ]}
    >
      <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-accent-amber/10 border border-accent-amber/25">
        <span className="text-xs font-medium text-accent-amber/80">Preview mode</span>
        <button
          onClick={() => navigate(`/admin/dashboards/${config.slug}/edit`)}
          className="flex items-center gap-1.5 text-xs font-medium text-accent-amber/80 hover:text-accent-amber transition-colors cursor-pointer"
        >
          <Pencil className="h-3 w-3" />
          Back to Edit
        </button>
      </div>
      <div className="w-full py-6">
        {hiddenCount > 0 && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => mutateOverride((o) => ({ ...o, hiddenInstanceIds: [] }))}
              className="flex items-center gap-1.5 text-xs text-muted hover:text-primary transition-colors"
            >
              <EyeOff className="h-3.5 w-3.5" />
              {hiddenCount} hidden — restore all
            </button>
          </div>
        )}

        {/* Sections */}
        <DashboardFilterProvider>
          <ActiveFilterBar />
          <div className="flex flex-col gap-6">
            {sortedSections.map((section) => {
              const isCollapsed = collapsedSections.has(section.id);

              // Apply analyst layout override if present, otherwise use master
              const masterPlacements = section.placements;

              // Build placements with hidden ones marked
              const placementsWithHidden = masterPlacements.map((p) => ({
                ...p,
                hidden: override.hiddenInstanceIds.includes(placementKey(p)),
              }));

              return (
                <div key={section.id} className="flex flex-col">
                  {/* Section header */}
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <button
                      onClick={() => toggleCollapse(section.id)}
                      className="text-muted hover:text-primary transition-colors cursor-pointer"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <span className="text-sm font-semibold text-primary">{section.name}</span>
                  </div>

                  {/* Section body */}
                  {!isCollapsed && (
                    <GridLayoutCore
                      placements={placementsWithHidden}
                      mode="view"
                      onLayoutChange={(updated) => updateSectionLayout(section.id, updated)}
                      onRemoveWidget={(instanceId) => toggleHideWidget(instanceId)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </DashboardFilterProvider>
      </div>
    </AdminLayout>
  );
}
